/**
 * Comprehensive Token Management Service
 * Handles both JWT and Google Drive tokens with proper validation and automatic refresh
 * Ensures robust token management across multiple devices
 */

class TokenManager {
  constructor() {
    this.isInitialized = false
    this.refreshInProgress = false
    this.tokenRefreshQueue = []
    this.deviceId = this.generateDeviceId()
    
    // Token expiration tracking
    this.jwtExpirationTime = null
    this.googleDriveExpirationTime = null
    
    // Refresh timing tracking for rate limiting
    this.lastGoogleDriveRefreshTime = null
    
    // Auto-refresh intervals
    this.jwtRefreshInterval = null
    this.googleDriveRefreshInterval = null
    
    // Event listeners
    this.eventListeners = new Map()
    
    this.initialize()
  }
  
  /**
   * Initialize token manager
   */
  initialize() {
    if (this.isInitialized) return
    
    try {
      // Load existing tokens
      this.loadStoredTokens()
      
      // Set up automatic refresh
      this.setupAutoRefresh()
      
      // Start monitoring
      this.startTokenMonitoring()
      
      this.isInitialized = true
      console.log('✅ TokenManager: Initialized successfully')
      
    } catch (error) {
      console.error('❌ TokenManager: Initialization failed:', error)
    }
  }
  
  /**
   * Generate unique device ID
   */
  generateDeviceId() {
    let deviceId = localStorage.getItem('deviceId')
    if (!deviceId) {
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15)
      localStorage.setItem('deviceId', deviceId)
    }
    return deviceId
  }
  
  /**
   * Load tokens from localStorage
   */
  loadStoredTokens() {
    try {
      // JWT tokens
      const authToken = localStorage.getItem('authToken')
      const userRefreshToken = localStorage.getItem('userRefreshToken')
      
      if (authToken) {
        this.setJWTToken(authToken)
      }
      
      if (userRefreshToken) {
        this.setJWTRefreshToken(userRefreshToken)
      }
      
      // Google Drive tokens
      const cloudAccessToken = localStorage.getItem('cloudAccessToken')
      const cloudRefreshToken = localStorage.getItem('cloudRefreshToken')
      
      if (cloudAccessToken) {
        this.setGoogleDriveToken(cloudAccessToken)
      }
      
      if (cloudRefreshToken) {
        this.setGoogleDriveRefreshToken(cloudRefreshToken)
      }
      
      console.log('✅ TokenManager: Loaded stored tokens')
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to load stored tokens:', error)
    }
  }
  
  /**
   * Set JWT access token
   */
  setJWTToken(token) {
    if (!token || token === localStorage.getItem('authToken')) return
    
    try {
      localStorage.setItem('authToken', token)
      
      // Parse JWT to get expiration
      const payload = this.parseJWT(token)
      if (payload && payload.exp) {
        this.jwtExpirationTime = new Date(payload.exp * 1000)
        console.log('🔑 TokenManager: JWT token expires at:', this.jwtExpirationTime)
      }
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to set JWT token:', error)
    }
  }
  
  /**
   * Set JWT refresh token
   */
  setJWTRefreshToken(token) {
    if (!token || token === localStorage.getItem('userRefreshToken')) return
    
    try {
      localStorage.setItem('userRefreshToken', token)
      console.log('🔑 TokenManager: JWT refresh token set')
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to set JWT refresh token:', error)
    }
  }
  
  /**
   * Set Google Drive access token
   */
  setGoogleDriveToken(token) {
    if (!token) {
      console.warn('⚠️ TokenManager: Attempted to set null/undefined Google Drive token')
      return
    }
    
    console.log('🔑 TokenManager: Setting Google Drive token')
    console.log('🔍 TokenManager: Token type:', typeof token)
    console.log('🔍 TokenManager: Token length:', token?.length)
    
    try {
      localStorage.setItem('cloudAccessToken', token)
      
      // Google Drive tokens typically expire in 1 hour, but Google may invalidate them earlier
      // Be more conservative: set expiry to 45 minutes to prevent 401s
      this.googleDriveExpirationTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes
      
      // CRITICAL FIX: Always set localStorage expiry so GoogleDriveAPI can check proactively
      // This ensures that even if the token already exists in localStorage, we still set the expiry
      const expiryTime = new Date(Date.now() + 45 * 60 * 1000)
      localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
      
      console.log('🔑 TokenManager: Google Drive token expires at:', this.googleDriveExpirationTime)
      console.log('🔑 TokenManager: Set localStorage googleDriveTokenExpiry to:', expiryTime.toISOString())
      
      // CRITICAL FIX: Emit immediate token update event for faster synchronization
      try {
        const immediateEvent = new CustomEvent('googleDriveTokenImmediatelyUpdated', {
          detail: { 
            accessToken: token, 
            refreshToken: localStorage.getItem('cloudRefreshToken'),
            timestamp: Date.now(),
            source: 'tokenManager_set'
          }
        })
        window.dispatchEvent(immediateEvent)
        console.log('✅ TokenManager: Emitted immediate token update event')
      } catch (eventError) {
        console.warn('⚠️ TokenManager: Failed to emit immediate update event:', eventError)
      }
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to set Google Drive token:', error)
    }
  }
  
  /**
   * Set Google Drive refresh token
   */
  setGoogleDriveRefreshToken(token) {
    if (!token || token === localStorage.getItem('cloudRefreshToken')) return
    
    try {
      localStorage.setItem('cloudRefreshToken', token)
      console.log('🔑 TokenManager: Google Drive refresh token set')
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to set Google Drive refresh token:', error)
    }
  }
  
  /**
   * Get JWT access token
   */
  getJWTToken() {
    const token = localStorage.getItem('authToken')
    if (!token) return null
    
    // Check if token is expired
    if (this.jwtExpirationTime && new Date() > this.jwtExpirationTime) {
      console.log('⚠️ TokenManager: JWT token expired, attempting refresh')
      this.refreshJWTToken()
      return null
    }
    
    return token
  }
  
  /**
   * Get Google Drive access token
   */
  getGoogleDriveToken() {
    const token = localStorage.getItem('cloudAccessToken')
    if (!token) return null
    
    // Check if token is expired
    if (this.googleDriveExpirationTime && new Date() > this.googleDriveExpirationTime) {
      console.log('⚠️ TokenManager: Google Drive token expired, attempting refresh')
      this.refreshGoogleDriveToken()
      return null
    }
    
    return token
  }
  
  /**
   * Check if JWT token needs refresh
   */
  shouldRefreshJWT() {
    if (!this.jwtExpirationTime) return false
    
    const now = new Date()
    const timeUntilExpiry = this.jwtExpirationTime.getTime() - now.getTime()
    const fiveMinutes = 5 * 60 * 1000
    
    return timeUntilExpiry < fiveMinutes
  }
  
  /**
   * Check if Google Drive token needs refresh
   */
  shouldRefreshGoogleDrive() {
    if (!this.googleDriveExpirationTime) return false
    
    const now = new Date()
    const timeUntilExpiry = this.googleDriveExpirationTime.getTime() - now.getTime()
    
    // Reasonable rate limiting for Google Drive tokens
    // Google allows frequent refreshes, but we still want to be respectful
    const fiveMinutes = 5 * 60 * 1000
    const twoMinutes = 2 * 60 * 1000
    
    // Check if we're within 5 minutes of expiry
    if (timeUntilExpiry < fiveMinutes) {
      // Additional safety check: ensure we haven't refreshed too recently
      const lastRefresh = this.lastGoogleDriveRefreshTime || 0
      const timeSinceLastRefresh = now.getTime() - lastRefresh
      
      // Only allow refresh if it's been at least 2 minutes since last refresh
      if (timeSinceLastRefresh >= twoMinutes) {
        console.log('🔄 TokenManager: Google Drive token needs refresh (expires in', Math.round(timeUntilExpiry / 60000), 'minutes)')
        return true
      } else {
        console.log('⏰ TokenManager: Google Drive refresh blocked - too recent (last refresh was', Math.round(timeSinceLastRefresh / 60000), 'minutes ago)')
        return false
      }
    }
    
    return false
  }
  
  /**
   * Refresh JWT token
   */
  async refreshJWTToken() {
    if (this.refreshInProgress) {
      return new Promise((resolve, reject) => {
        this.tokenRefreshQueue.push({ type: 'jwt', resolve, reject })
      })
    }
    
    this.refreshInProgress = true
    
    try {
      const refreshToken = localStorage.getItem('userRefreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }
      
      console.log('🔄 TokenManager: Refreshing JWT token')
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refreshToken: refreshToken
        })
      })
      
      if (!response.ok) {
        throw new Error(`JWT refresh failed: ${response.status}`)
      }
      
      const data = await response.json()
      
      // Update tokens
      this.setJWTToken(data.token)
      if (data.refreshToken) {
        this.setJWTRefreshToken(data.refreshToken)
      }
      
      // Update user data
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user))
      }
      
      // Handle Google Drive tokens if provided
      if (data.cloudAccessToken) {
        localStorage.setItem('cloudAccessToken', data.cloudAccessToken)
        
        // CRITICAL: Also set the expiry in localStorage for proactive refresh
        const expiryTime = new Date(Date.now() + 45 * 60 * 1000) // 45 minutes
        localStorage.setItem('googleDriveTokenExpiry', expiryTime.toISOString())
        
        console.log('✅ TokenManager: Google Drive access token refreshed')
        
        if (data.cloudRefreshToken) {
          localStorage.setItem('cloudRefreshToken', data.cloudRefreshToken)
          console.log('✅ TokenManager: Google Drive refresh token refreshed')
        }
      }
      
      console.log('✅ TokenManager: JWT token refreshed successfully')
      
      // Process queued requests
      this.processRefreshQueue('jwt', data.token)
      
      // Emit token refreshed event with all tokens
      this.emitTokenRefreshedEvent(data.token, data.user, data.cloudAccessToken, data.cloudRefreshToken)
      
      return data.token
      
    } catch (error) {
      console.error('❌ TokenManager: JWT refresh failed:', error)
      
      // Clear invalid tokens
      this.clearJWTTokens()
      
      // Process queued requests with error
      this.processRefreshQueue('jwt', null, error)
      
      throw error
      
    } finally {
      this.refreshInProgress = false
    }
  }
  
  /**
   * Emit token refreshed event for AuthContext
   */
  emitTokenRefreshedEvent(token, userInfo, cloudAccessToken, cloudRefreshToken) {
    try {
      const event = new CustomEvent('tokenRefreshed', {
        detail: {
          token,
          userInfo,
          cloudAccessToken,
          cloudRefreshToken
        }
      })
      window.dispatchEvent(event)
      console.log('✅ TokenManager: Emitted tokenRefreshed event with Google Drive tokens')
    } catch (error) {
      console.error('❌ TokenManager: Failed to emit tokenRefreshed event:', error)
    }
  }
  
  /**
   * Refresh Google Drive token
   */
  async refreshGoogleDriveToken(force = false) {
    if (this.refreshInProgress) {
      return new Promise((resolve, reject) => {
        this.tokenRefreshQueue.push({ type: 'google_drive', resolve, reject })
      })
    }
    
    // Check rate limiting unless forced
    if (!force && !this.shouldRefreshGoogleDrive()) {
      console.log('⏰ TokenManager: Google Drive refresh blocked by rate limiting')
      return null
    }
    
    this.refreshInProgress = true
    
    try {
      const refreshToken = localStorage.getItem('cloudRefreshToken')
      if (!refreshToken) {
        throw new Error('No Google Drive refresh token available')
      }
      
      console.log('🔄 TokenManager: Refreshing Google Drive token' + (force ? ' (forced)' : ''))
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${this.getJWTToken()}`
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Google Drive refresh failed: ${response.status} - ${errorData.error || 'Unknown error'}`)
      }
      
      const data = await response.json()
      
      // Update tokens
      this.setGoogleDriveToken(data.accessToken)
      if (data.refreshToken) {
        this.setGoogleDriveRefreshToken(data.refreshToken)
      }
      
      // Track the refresh time for rate limiting
      this.lastGoogleDriveRefreshTime = Date.now()
      
      console.log('✅ TokenManager: Google Drive token refreshed successfully')
      
      // Process queued requests
      this.processRefreshQueue('google_drive', data.accessToken)
      
      // Emit token refreshed event for Google Drive tokens
      this.emitTokenRefreshedEvent(null, null, data.accessToken, data.refreshToken || localStorage.getItem('cloudRefreshToken'))
      
      // ALSO emit the googleDriveTokenRefreshed event that GoogleDriveAPI listens for
      try {
        const event = new CustomEvent('googleDriveTokenRefreshed', {
          detail: {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || localStorage.getItem('cloudRefreshToken')
          }
        })
        window.dispatchEvent(event)
        console.log('✅ TokenManager: Also emitted googleDriveTokenRefreshed event for GoogleDriveAPI')
      } catch (error) {
        console.error('❌ TokenManager: Failed to emit googleDriveTokenRefreshed event:', error)
      }
      
      return data.accessToken
      
    } catch (error) {
      console.error('❌ TokenManager: Google Drive refresh failed:', error)
      
      // Clear invalid tokens
      this.clearGoogleDriveTokens()
      
      // Process queued requests with error
      this.processRefreshQueue('google_drive', null, error)
      
      throw error
      
    } finally {
      this.refreshInProgress = false
    }
  }
  
  /**
   * Emergency refresh Google Drive token (bypasses rate limiting)
   */
  async emergencyRefreshGoogleDriveToken() {
    console.log('🚨 TokenManager: Emergency Google Drive token refresh requested')
    return this.refreshGoogleDriveToken(true)
  }
  
  /**
   * Check if emergency refresh is needed (for debugging)
   */
  canEmergencyRefresh() {
    const now = new Date()
    const lastRefresh = this.lastGoogleDriveRefreshTime || 0
    const timeSinceLastRefresh = now.getTime() - lastRefresh
    const twoMinutes = 2 * 60 * 1000
    
    return timeSinceLastRefresh >= twoMinutes
  }
  
  /**
   * Process queued refresh requests
   */
  processRefreshQueue(type, token, error = null) {
    const queue = this.tokenRefreshQueue.filter(item => item.type === type)
    this.tokenRefreshQueue = this.tokenRefreshQueue.filter(item => item.type !== type)
    
    queue.forEach(item => {
      if (error) {
        item.reject(error)
      } else {
        item.resolve(token)
      }
    })
  }
  
  /**
   * Setup automatic token refresh
   */
  setupAutoRefresh() {
    // JWT auto-refresh (check every minute)
    this.jwtRefreshInterval = setInterval(() => {
      if (this.shouldRefreshJWT()) {
        this.refreshJWTToken().catch(console.error)
      }
    }, 60000)
    
    // Google Drive auto-refresh (check every 2 minutes)
    this.googleDriveRefreshInterval = setInterval(() => {
      if (this.shouldRefreshGoogleDrive()) {
        this.refreshGoogleDriveToken().catch(console.error)
      }
    }, 120000)
    
    console.log('✅ TokenManager: Auto-refresh intervals set up')
  }
  
  /**
   * Start token monitoring
   */
  startTokenMonitoring() {
    try {
      // Monitor localStorage changes
      const originalSetItem = localStorage.setItem
      const originalRemoveItem = localStorage.removeItem
      
      const self = this
      
      // Flag to prevent recursive calls
      let isInternalUpdate = false
      let monitoringActive = true
      
      localStorage.setItem = function(key, value) {
        // Call original method first
        originalSetItem.call(this, key, value)
        
        // Only process if monitoring is active and this is not an internal update
        if (monitoringActive && !isInternalUpdate) {
          try {
            isInternalUpdate = true
            
            if (key === 'authToken') {
              self.setJWTToken(value)
            } else if (key === 'userRefreshToken') {
              self.setJWTRefreshToken(value)
            } else if (key === 'cloudAccessToken') {
              self.setGoogleDriveToken(value)
            } else if (key === 'cloudRefreshToken') {
              self.setGoogleDriveRefreshToken(value)
            }
          } catch (error) {
            console.error('❌ TokenManager: Error in setItem monitoring:', error)
          } finally {
            isInternalUpdate = false
          }
        }
      }
      
      localStorage.removeItem = function(key) {
        // Call original method first
        originalRemoveItem.call(this, key)
        
        // Only process if monitoring is active and this is not an internal update
        if (monitoringActive && !isInternalUpdate) {
          try {
            isInternalUpdate = true
            
            if (key === 'authToken' || key === 'userRefreshToken') {
              self.clearJWTTokens()
            } else if (key === 'cloudAccessToken' || key === 'cloudRefreshToken') {
              self.clearGoogleDriveTokens()
            }
          } catch (error) {
            console.error('❌ TokenManager: Error in removeItem monitoring:', error)
          } finally {
            isInternalUpdate = false
          }
        }
      }
      
      // Store references for cleanup
      this._originalSetItem = originalSetItem
      this._originalRemoveItem = originalRemoveItem
      this._monitoringActive = monitoringActive
      
      console.log('✅ TokenManager: Token monitoring started')
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to start token monitoring:', error)
    }
  }
  
  /**
   * Stop token monitoring
   */
  stopTokenMonitoring() {
    try {
      if (this._originalSetItem && this._originalRemoveItem) {
        // Restore original methods
        localStorage.setItem = this._originalSetItem
        localStorage.removeItem = this._originalRemoveItem
        
        this._monitoringActive = false
        console.log('✅ TokenManager: Token monitoring stopped')
      }
    } catch (error) {
      console.error('❌ TokenManager: Failed to stop token monitoring:', error)
    }
  }
  
  /**
   * Clear JWT tokens
   */
  clearJWTTokens() {
    localStorage.removeItem('authToken')
    localStorage.removeItem('userRefreshToken')
    localStorage.removeItem('user')
    
    this.jwtExpirationTime = null
    
    // Also clear Google Drive tokens for complete logout
    this.clearGoogleDriveTokens()
    
    console.log('🧹 TokenManager: JWT tokens and Google Drive tokens cleared')
  }
  
  /**
   * Clear Google Drive tokens
   */
  clearGoogleDriveTokens() {
    localStorage.removeItem('cloudAccessToken')
    localStorage.removeItem('cloudRefreshToken')
    localStorage.removeItem('cloudUserInfo')
    localStorage.removeItem('googleDriveTokenExpiry')
    
    this.googleDriveExpirationTime = null
    
    console.log('🧹 TokenManager: Google Drive tokens cleared')
  }
  
  /**
   * Clear all tokens (JWT + Google Drive)
   */
  clearAllTokens() {
    this.clearJWTTokens()
    // Note: clearJWTTokens already calls clearGoogleDriveTokens
    console.log('🧹 TokenManager: All tokens cleared')
  }
  
  /**
   * Parse JWT token to get payload
   */
  parseJWT(token) {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.warn('⚠️ TokenManager: Failed to parse JWT:', error)
      return null
    }
  }
  
  /**
   * Get token status
   */
  getTokenStatus() {
    return {
      jwt: {
        hasToken: !!localStorage.getItem('authToken'),
        hasRefreshToken: !!localStorage.getItem('userRefreshToken'),
        expiresAt: this.jwtExpirationTime,
        needsRefresh: this.shouldRefreshJWT()
      },
      googleDrive: {
        hasToken: !!localStorage.getItem('cloudAccessToken'),
        hasRefreshToken: !!localStorage.getItem('cloudRefreshToken'),
        expiresAt: this.googleDriveExpirationTime,
        needsRefresh: this.shouldRefreshGoogleDrive()
      },
      deviceId: this.deviceId,
      isInitialized: this.isInitialized,
      monitoring: {
        active: this._monitoringActive || false,
        hasOriginalMethods: !!(this._originalSetItem && this._originalRemoveItem)
      }
    }
  }
  
  /**
   * Debug monitoring system
   */
  debugMonitoring() {
    console.log('🔍 TokenManager: Monitoring Debug Info')
    console.log('- Monitoring Active:', this._monitoringActive)
    console.log('- Has Original SetItem:', !!this._originalSetItem)
    console.log('- Has Original RemoveItem:', !!this._originalRemoveItem)
    console.log('- Is Internal Update Flag:', false) // This would be a local variable
    console.log('- Current localStorage.setItem:', localStorage.setItem.toString().substring(0, 100) + '...')
    console.log('- Current localStorage.removeItem:', localStorage.removeItem.toString().substring(0, 100) + '...')
  }
  
  /**
   * Reset monitoring system (use if monitoring gets corrupted)
   */
  resetMonitoring() {
    try {
      console.log('🔄 TokenManager: Resetting monitoring system...')
      
      // Stop current monitoring
      this.stopTokenMonitoring()
      
      // Clear all references
      this._originalSetItem = null
      this._originalRemoveItem = null
      this._monitoringActive = false
      
      // Restart monitoring
      this.startTokenMonitoring()
      
      console.log('✅ TokenManager: Monitoring system reset successfully')
      
    } catch (error) {
      console.error('❌ TokenManager: Failed to reset monitoring:', error)
    }
  }
  
  /**
   * Emergency stop all monitoring (use if infinite loops occur)
   */
  emergencyStop() {
    try {
      console.log('🚨 TokenManager: Emergency stop initiated...')
      
      // Restore original localStorage methods immediately
      if (this._originalSetItem) {
        localStorage.setItem = this._originalSetItem
      }
      if (this._originalRemoveItem) {
        localStorage.removeItem = this._originalRemoveItem
      }
      
      // Clear all references
      this._originalSetItem = null
      this._originalRemoveItem = null
      this._monitoringActive = false
      
      // Stop intervals
      if (this.jwtRefreshInterval) {
        clearInterval(this.jwtRefreshInterval)
        this.jwtRefreshInterval = null
      }
      if (this.googleDriveRefreshInterval) {
        clearInterval(this.googleDriveRefreshInterval)
        this.googleDriveRefreshInterval = null
      }
      
      console.log('🚨 TokenManager: Emergency stop completed - monitoring disabled')
      
    } catch (error) {
      console.error('❌ TokenManager: Emergency stop failed:', error)
    }
  }
  
  /**
   * Cleanup resources
   */
  cleanup() {
    try {
      // Stop token monitoring
      this.stopTokenMonitoring()
      
      // Clear intervals
      if (this.jwtRefreshInterval) {
        clearInterval(this.jwtRefreshInterval)
        this.jwtRefreshInterval = null
      }
      
      if (this.googleDriveRefreshInterval) {
        clearInterval(this.googleDriveRefreshInterval)
        this.googleDriveRefreshInterval = null
      }
      
      // Clear references
      this._originalSetItem = null
      this._originalRemoveItem = null
      this._monitoringActive = false
      
      console.log('🧹 TokenManager: Cleanup completed')
      
    } catch (error) {
      console.error('❌ TokenManager: Error during cleanup:', error)
    }
  }
}

// Export singleton instance
const tokenManager = new TokenManager()
export default tokenManager
